import { Close, Help, Info, Keyboard, Telegram } from '@mui/icons-material';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useHotkeys } from 'react-hotkeys-hook';

import { devContacts } from '@/configs/devContacts';
import { isMac } from '@/utils/isMac';

import { Button } from '../../components/Button';

import { shortcuts, tableCell, tips } from './config';

import s from './HelpModal.module.scss';

type HelpModalProps = {
  open: boolean;
  onClose: () => void;
};

export const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
  useHotkeys(isMac ? 'meta+enter' : 'alt+enter', (e) => {
    e.preventDefault();
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={'md'}
      sx={{
        '.MuiPaper-root': { borderRadius: '8px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className={s.title}>
          <Help />
          <div>Справка</div>
        </div>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={4}>
          {/* Горячие клавиши */}
          <Box>
            <div className={s.help__title}>
              <Keyboard />
              <h1>Горячие клавиши</h1>
            </div>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {tableCell.map((cell) => (
                      <TableCell key={cell.name} className={s.help__table_title}>
                        <p>{cell.name}</p>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shortcuts.map((shortcut, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={shortcut.key}
                          size="small"
                          variant="outlined"
                          sx={{ fontFamily: 'monospace' }}
                        />
                      </TableCell>
                      <TableCell className={s.help__table_description}>
                        <p>{shortcut.description}</p>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={shortcut.category}
                          size="small"
                          variant="outlined"
                          style={{
                            color: 'var(--accent-color)',
                            borderColor: 'var(--accent-color)',
                          }}
                          sx={{ fontFamily: 'Montserrat' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Divider />

          {/* Советы по использованию */}
          <Box>
            <div className={s.help__title}>
              <Info />
              <h1>Советы по использованию</h1>
            </div>

            <Box display="flex" flexDirection="column" gap={2}>
              {tips.map((tip, index) => (
                <Paper
                  className={s.help__advice}
                  key={index}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: '8px' }}
                >
                  <h2>{tip.title}</h2>
                  <p>{tip.description}</p>
                </Paper>
              ))}
            </Box>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={1} p={5} color={'var(--secondary-color)'}>
          <div className={s.authors}>
            <div>Авторы:</div>
            <div className={s.author__container}>
              {devContacts.map((author) => (
                <div key={author.name} className={s.author}>
                  <a href={author.tg} target="_blank" rel="noopener noreferrer">
                    <p>{author.name}</p>
                    <Telegram />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Понятно
        </Button>
      </DialogActions>
    </Dialog>
  );
};
